import React from 'react';
import { createPortal } from 'react-dom';
import type { ProgramProgressCell } from '../../types/domain';
import { deleteEmptyPresentationBindingSelections } from '../../services/programEmptyPresentationBindingService';
import {
  deleteProgramProgressCellsAuditSelections,
  deletePresentationAuditSelections,
  fetchAnnualProgressAuditSnapshot,
  type AnnualProgressAuditSnapshot,
} from '../../services/programPresentationAuditService';
import {
  deleteEmptyWorksheetBindingSelections,
  deleteWorksheetAuditSelections,
} from '../../services/programWorksheetAuditService';
import ModalBodyLoadingSpinner from '../common/ModalBodyLoadingSpinner';
import PresentationAuditSection from '../program/PresentationAuditSection';
import WorksheetAuditSection from '../program/WorksheetAuditSection';

type AuditColumnDef = {
  key: string;
  dbColumn: string;
  hint?: string;
  maxLen?: number;
  identity?: boolean;
};

function formatAuditCellValue(value: unknown, _maxLen = 28): { display: string; title?: string } {
  if (value === null || value === undefined) return { display: '—' };
  if (typeof value === 'boolean') return { display: value ? 'true' : 'false' };
  if (typeof value === 'number') return { display: String(value) };
  if (typeof value === 'object') {
    const full = JSON.stringify(value);
    return { display: full, title: full };
  }
  const full = String(value);
  const trimmed = full.trim();
  if (!trimmed) return { display: '—' };
  return { display: full, title: full };
}

const PROGRAM_PROGRESS_CELL_COLUMNS: AuditColumnDef[] = [
  { key: 'id', dbColumn: 'id', hint: 'PK', maxLen: 8, identity: true },
  { key: 'monday_iso', dbColumn: 'monday_iso', hint: '주', identity: true, maxLen: 12 },
  { key: 'weekday_key', dbColumn: 'weekday_key', hint: '요일', identity: true, maxLen: 8 },
  { key: 'slot_index', dbColumn: 'slot_index', hint: '교시', identity: true, maxLen: 4 },
  { key: 'timetable_text', dbColumn: 'timetable_text', maxLen: 20 },
  { key: 'unit_text', dbColumn: 'unit_text', maxLen: 20 },
  { key: 'learning_text', dbColumn: 'learning_text', hint: '학습 본문(평문)', maxLen: 24 },
  { key: 'learning_note', dbColumn: 'learning_note', hint: '학습 메모', maxLen: 20 },
  { key: 'presentation_ref_id', dbColumn: 'presentation_ref_id', hint: '프레젠테이션 ref', maxLen: 12 },
  { key: 'worksheet_ref_id', dbColumn: 'worksheet_ref_id', hint: '활동지 ref', maxLen: 12 },
  { key: 'page_text', dbColumn: 'page_text', maxLen: 16 },
  { key: 'record_class_text', dbColumn: 'record_class_text', maxLen: 18 },
  { key: 'updated_at', dbColumn: 'updated_at', maxLen: 18 },
];

function ProgressCellAuditTable(props: {
  rows: ProgramProgressCell[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  const { rows, selected, onToggle } = props;
  return (
    <table className="cp-program-data-cleanup-table cp-program-data-cleanup-table--db">
      <thead>
        <tr>
          <th className="cp-program-data-cleanup-col-check" scope="col" aria-label="선택" />
          {PROGRAM_PROGRESS_CELL_COLUMNS.map((col) => (
            <th
              key={col.dbColumn}
              scope="col"
              className={
                col.identity
                  ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                  : 'cp-program-data-cleanup-col-db'
              }
              title={col.hint ? `${col.dbColumn} — ${col.hint}` : col.dbColumn}
            >
              <code>{col.dbColumn}</code>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const id = (row.id ?? '').trim();
          const key = id ? `pc:${id}` : '';
          return (
            <tr key={key || `${row.monday_iso}-${row.weekday_key}-${row.slot_index}`}>
              <td className="cp-program-data-cleanup-col-check">
                <input
                  type="checkbox"
                  checked={!!key && selected.has(key)}
                  onChange={() => key && onToggle(key)}
                  disabled={!key}
                  aria-label={`프로그램 진도셀 ${row.monday_iso} ${row.weekday_key} ${row.slot_index}`}
                />
              </td>
              {PROGRAM_PROGRESS_CELL_COLUMNS.map((col) => {
                const { display, title } = formatAuditCellValue(
                  (row as unknown as Record<string, unknown>)[col.key],
                  col.maxLen ?? 28
                );
                return (
                  <td
                    key={col.dbColumn}
                    className={
                      col.identity
                        ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                        : 'cp-program-data-cleanup-col-db'
                    }
                    title={title}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export type AnnualProgressDataCleanupModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  onCleanupComplete?: () => void;
};

const AnnualProgressDataCleanupModal: React.FC<AnnualProgressDataCleanupModalProps> = ({
  open,
  onClose,
  workspaceId,
  academicYear,
  termNumber,
  gradeId,
  onCleanupComplete,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<AnnualProgressAuditSnapshot | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = React.useState(false);

  React.useLayoutEffect(() => {
    if (open && gradeId.trim()) setLoading(true);
  }, [open, gradeId]);

  React.useEffect(() => {
    if (!open || !gradeId.trim()) {
      if (!open) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    void (async () => {
      try {
        const data = await fetchAnnualProgressAuditSnapshot({
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
        });
        if (!cancelled) setSnapshot(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, academicYear, termNumber, gradeId]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectSectionKeys = (keys: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (checked) next.add(k);
        else next.delete(k);
      });
      return next;
    });
  };

  const handleDelete = async () => {
    if (!snapshot || selected.size === 0) return;
    const pcIds: string[] = [];
    const slideKeys: string[] = [];
    const emptyRefKeys: string[] = [];
    const worksheetKeys: string[] = [];
    const emptyWorksheetRefKeys: string[] = [];
    selected.forEach((k) => {
      if (k.startsWith('pc:')) pcIds.push(k.slice(3));
      else if (k.startsWith('slide:')) slideKeys.push(k);
      else if (k.startsWith('empty-ref:')) emptyRefKeys.push(k);
      else if (k.startsWith('worksheet:')) worksheetKeys.push(k);
      else if (k.startsWith('empty-worksheet-ref:')) emptyWorksheetRefKeys.push(k);
    });
    const msg = `선택한 ${selected.size}건을 DB에서 삭제합니다. 프로그램 진도셀·슬라이드·활동지 삭제 시 연결 문서도 함께 지워지며, 빈 프레젠테이션·활동지 연결은 ref만 제거합니다. 계속할까요?`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    setError(null);
    try {
      if (emptyRefKeys.length && snapshot) {
        await deleteEmptyPresentationBindingSelections({
          workspaceId,
          selectionKeys: emptyRefKeys,
          scopedRows: snapshot.scopedRows,
          fixedRows: snapshot.fixedRows,
          programProgressCells: snapshot.programProgressCells,
        });
      }
      if (emptyWorksheetRefKeys.length && snapshot) {
        await deleteEmptyWorksheetBindingSelections({
          workspaceId,
          selectionKeys: emptyWorksheetRefKeys,
          scopedRows: snapshot.scopedRows,
          fixedRows: snapshot.fixedRows,
          programProgressCells: snapshot.programProgressCells,
        });
      }
      if (slideKeys.length) {
        await deletePresentationAuditSelections({ workspaceId, selectionKeys: slideKeys });
      }
      if (worksheetKeys.length) {
        await deleteWorksheetAuditSelections({ workspaceId, selectionKeys: worksheetKeys });
      }
      if (pcIds.length) {
        await deleteProgramProgressCellsAuditSelections({ workspaceId, cellIds: pcIds });
      }
      onCleanupComplete?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const pcKeys = (snapshot?.programProgressCells ?? [])
    .filter((r) => (r.id ?? '').trim())
    .map((r) => `pc:${r.id}`);

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget && !deleting) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-data-cleanup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-annual-progress-data-cleanup-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-annual-progress-data-cleanup-title">연간진도표 데이터 정리</h3>
        <p className="page-desc cp-program-data-cleanup-intro">
          현재 학년·학기 연간진도표가 참조하는 DB 원본을 조회합니다.{' '}
          <strong>프로그램 진도셀</strong>(<code>program_progress_cells</code>)은 연간진도표·레거시 경로에 남은 행이며, 편제
          프로그램 원본(<code>program_scoped_content_rows</code>)과 겹치거나 고아 슬라이드가 붙어 있을 수 있습니다.{' '}
          <strong>프레젠테이션 슬라이드</strong>·<strong>활동지</strong>는 ref별 문서 묶음입니다. 고아 항목을 확인한 뒤 선택 삭제하세요.
        </p>
        {error ? (
          <p className="cp-program-data-cleanup-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="cp-modal-body cp-program-data-cleanup-body" aria-busy={loading || undefined}>
          {loading ? (
            <ModalBodyLoadingSpinner label="연간진도표 데이터 정리 불러오는 중" />
          ) : snapshot ? (
            <>
              <section className="cp-program-data-cleanup-section">
                <div className="cp-program-data-cleanup-section-head">
                  <h4>프로그램 진도셀 — program_progress_cells ({snapshot.programProgressCells.length}건)</h4>
                  {pcKeys.length ? (
                    <button
                      type="button"
                      className="cp-btn-secondary cp-program-data-cleanup-select-all"
                      onClick={() => selectSectionKeys(pcKeys, pcKeys.some((k) => !selected.has(k)))}
                    >
                      {pcKeys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
                    </button>
                  ) : null}
                </div>
                <p className="page-desc cp-program-data-cleanup-section-desc">
                  연간진도표가 읽거나(행동특성 등) 예전 프레젠테이션 저장 경로에 쓰인 프로그램 진도셀입니다. 편제
                  프로그램 페이지와 내용이 이중으로 있을 수 있으니 삭제 전 고아 슬라이드·프로그램 원본을 확인하세요.
                </p>
                {snapshot.programProgressCells.length ? (
                  <div className="cp-program-data-cleanup-table-wrap">
                    <ProgressCellAuditTable rows={snapshot.programProgressCells} selected={selected} onToggle={toggle} />
                  </div>
                ) : (
                  <p className="page-desc">진도셀이 없습니다.</p>
                )}
              </section>

              <PresentationAuditSection
                entries={snapshot.presentationEntries}
                selected={selected}
                onToggle={toggle}
                onSelectSectionKeys={selectSectionKeys}
              />

              <WorksheetAuditSection
                entries={snapshot.worksheetEntries}
                selected={selected}
                onToggle={toggle}
                onSelectSectionKeys={selectSectionKeys}
              />
            </>
          ) : (
            <p className="page-desc">데이터가 없습니다.</p>
          )}
        </div>
        <div className="cp-modal-footer cp-program-data-cleanup-footer">
          <button type="button" className="cp-btn-secondary" onClick={() => onClose()} disabled={deleting}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={!selected.size || deleting || loading}
            onClick={() => void handleDelete()}
          >
            {deleting ? '삭제 중…' : `선택 삭제 (${selected.size})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnualProgressDataCleanupModal;
